import { buttonBaseClasses } from '@mui/material/ButtonBase';
import type { CSSObject, Theme } from '@mui/material/styles';
import { switchClasses } from '@mui/material/Switch';

import { sizeVariants } from 'theme/theme.helpers';
import type { ComponentOverrides } from 'theme/theme.types';

export const SWITCH_TRACK_PX = {
  medium: { width: 36, height: 20 },
  small: { width: 28, height: 16 },
} as const;

export type SwitchSize = keyof typeof SWITCH_TRACK_PX;

export const SWITCH_THUMB_PX = {
  medium: 16,
  small: 12,
} as const satisfies Record<SwitchSize, number>;

export const SWITCH_TRACK_PADDING_PX = 2;

export const SWITCH_LABEL_GAP_PX = 6;

export const SWITCH_MIN_TARGET_PX = 24;

const TRACK_RADIUS_PX = 999;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const switchTransition = (theme: Theme, props: string[]) => ({
  transition: theme.transitions.create(props, {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  [REDUCED_MOTION]: { transition: 'none' },
});

const CHECKED = `.${switchClasses.checked}`;
const DISABLED = `.${switchClasses.disabled}`;
const FOCUS_VISIBLE = `.${buttonBaseClasses.focusVisible}`;
const TRACK = `.${switchClasses.track}`;
const THUMB = `.${switchClasses.thumb}`;
const INPUT = `.${switchClasses.input}`;

export const switchThumbTravelPx = (size: SwitchSize) =>
  SWITCH_TRACK_PX[size].width -
  SWITCH_THUMB_PX[size] -
  SWITCH_TRACK_PADDING_PX * 2;

const switchBaseSizeStyle = (size: SwitchSize): CSSObject => {
  const { width, height } = SWITCH_TRACK_PX[size];
  const travel = switchThumbTravelPx(size);

  return {
    [`&&${CHECKED}`]: { transform: `translateX(${travel}px)` },
    [`&& ${INPUT}`]: {
      top: (height - SWITCH_MIN_TARGET_PX) / 2,
      left: 0,
      width,
      height: SWITCH_MIN_TARGET_PX,
    },
    [`&&${CHECKED} ${INPUT}`]: { left: -travel },
  };
};

export const MuiSwitch: ComponentOverrides['MuiSwitch'] = {
  defaultProps: {
    disableRipple: true,
  },
  styleOverrides: {
    root: {
      padding: 0,
      overflow: 'visible',
      variants: sizeVariants(SWITCH_TRACK_PX, (size) => ({
        width: SWITCH_TRACK_PX[size].width,
        height: SWITCH_TRACK_PX[size].height,
      })),
    },
    switchBase: ({ theme }) => ({
      '&&': {
        padding: SWITCH_TRACK_PADDING_PX,
        ...switchTransition(theme, ['left', 'transform']),
      },
      [`&:hover, &${CHECKED}:hover`]: { backgroundColor: 'transparent' },
      [`&:hover + ${TRACK}, &:active + ${TRACK}, &${FOCUS_VISIBLE} + ${TRACK}`]:
        { backgroundColor: theme.palette.grey[400] },
      [`&${CHECKED} + ${TRACK}`]: {
        backgroundColor: theme.palette.primary.light,
      },
      [`&${CHECKED}:hover + ${TRACK}, &${CHECKED}${FOCUS_VISIBLE} + ${TRACK}`]:
        {
          backgroundColor: theme.palette.tokens.lime[500],
        },
      [`&${CHECKED}:active + ${TRACK}`]: {
        backgroundColor: theme.palette.tokens.lime[600],
      },
      [`&${DISABLED} + ${TRACK}, &${CHECKED}${DISABLED} + ${TRACK}`]: {
        backgroundColor: theme.palette.grey[200],
      },
      '@media (forced-colors: active)': {
        [`&${CHECKED} ${THUMB}`]: { backgroundColor: 'Highlight' },
        [`&${DISABLED} ${THUMB}`]: { backgroundColor: 'GrayText' },
      },
      variants: sizeVariants(SWITCH_TRACK_PX, switchBaseSizeStyle),
    }),
    thumb: ({ theme }) => ({
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1],
      '@media (forced-colors: active)': {
        forcedColorAdjust: 'none',
        backgroundColor: 'ButtonText',
      },
      variants: sizeVariants(SWITCH_THUMB_PX, (size) => ({
        '&&': { width: SWITCH_THUMB_PX[size], height: SWITCH_THUMB_PX[size] },
      })),
    }),
    track: ({ theme }) => ({
      borderRadius: TRACK_RADIUS_PX,
      backgroundColor: theme.palette.grey[300],
      ...switchTransition(theme, ['background-color']),
    }),
  },
};
